/**
 * Linear Copilot's connection to Talkie over the direct bridge (`@talkie/agentic-app-transport`).
 * Build artifact: `frontend/bridge.bundle.js` (`npm run build`). The handlers live in `tools.ts`;
 * this file registers them, starts the bridge, tells app.js how it is going, and exposes the
 * host-run Linear grant and link opening to the unbundled modules.
 *
 * Host bootstrap: the host appends `#v=1&k=<key>&o=<origin>` to the iframe URL. Without it the page
 * is an ordinary standalone app; with it the page says hello and the status reads "connected" only
 * once the host has welcomed it.
 */

import { AppBridge, HostError, type BridgeStatus, type GrantStatus } from "@talkie/agentic-app-transport";
import { APP, LINEAR_GRANT } from "./app-identity";
import { HANDLERS } from "./tools";

declare global {
  interface Window {
    __linearHost?: LinearHost;
    __linearBridgeState?: BridgeState;
  }
}

/**
 * The HOST-RUN Linear grant and link opening, exposed to the unbundled modules.
 *
 * ⭐ WHY THIS LIVES IN THE BUNDLE. Only this file holds the bridge. `auth.js`, `linear.js` and
 * `app.js` are unbundled ES modules loaded directly by the browser, so they reach it the same way
 * the tool handlers reach them — through a window global. One bridge per page, not one per module.
 *
 * ⛔ `available()` is the ONLY platform test in the app, and it asks the host what it offers rather
 * than guessing where it is running. An "am I on desktop" flag would be a second source for the
 * same fact and would be wrong in exactly the case that matters — a native host whose install
 * declared no grants.
 */
interface LinearHost {
  /** True when the host holds this app's `linear` grant for it (the welcome lists the grant). */
  available: () => boolean;
  /**
   * Ask the host to run the sign-in. Resolves `true` when the person connected, `false` when they
   * declined, closed it or the host stopped waiting (every abandonment is one `cancelled` answer);
   * `signal` cancels the wait. Throws `no_host` when this host cannot complete the sign-in here.
   */
  connect: (signal?: AbortSignal) => Promise<boolean>;
  /** Whether a live grant is held. Reveals no token. */
  status: () => Promise<GrantStatus>;
  /** Revoke at the IdP where possible; the host deletes locally regardless. */
  disconnect: () => Promise<void>;
  /** POST a body to the grant's declared endpoint, host-credentialed. */
  call: (body: unknown, signal?: AbortSignal) => Promise<{ status: number; body: unknown }>;
  /**
   * Have the HOST open a link outside this frame. `false`: this host does not open links, so the
   * caller opens it itself. A refusal throws the host's `HostError` (its `code` says why).
   */
  openLink: (url: string) => Promise<boolean>;
}

// ─────────────────────────── Boot ──

/** What app.js shows in the "Talkie" status chip. */
export interface BridgeState { mode: BridgeStatus; reason?: string }

/**
 * Retained AND announced: this bundle runs before app.js has a listener (module scripts run in
 * order), so an event alone is lost. A late reader takes `window.__linearBridgeState`, a listener
 * gets `linear-copilot:bridge`.
 */
function publish(state: BridgeState): void {
  window.__linearBridgeState = state;
  window.dispatchEvent(new CustomEvent("linear-copilot:bridge", { detail: state }));
}

const bridge = new AppBridge({ appVersion: APP.version, onStatus: (mode, reason) => publish({ mode, reason }) });
for (const [name, handler] of Object.entries(HANDLERS)) bridge.registerTool(name, handler as Parameters<AppBridge["registerTool"]>[1]);

// ─────────────────────────── Host-run Linear grant ──

const grant = bridge.grant(LINEAR_GRANT);
/** The welcome's word on this app's grant: absent ⇒ the host holds none for this install. */
const offered = () => bridge.host?.grants.find((g) => g.name === LINEAR_GRANT);

function coded(code: string, message: string): Error {
  return Object.assign(new Error(message), { code });
}

const host: LinearHost = {
  available: () => offered() !== undefined,

  connect: async (signal?: AbortSignal) => {
    // The host builds the authorize URL from the DECLARED grant and owns the PKCE material, the
    // redirect and the tokens; the page names the grant and learns only how the person answered.
    if (!offered()?.connect) throw coded("no_host", "this Talkie app cannot complete Linear's sign-in here");
    try {
      return (await bridge.connect(LINEAR_GRANT, { signal })) === "accepted";
    } catch (error) {
      if (error instanceof HostError && error.code === "unsupported") throw coded("no_host", error.message);
      throw error;
    }
  },

  status: () => grant.status(),
  disconnect: () => grant.revoke(),
  call: (body, signal) => grant.call(body, { signal }),

  openLink: async (url: string) => {
    if (!bridge.host?.open_link) return false;
    await bridge.openLink(url);
    return true;
  },
};

window.__linearHost = host;
bridge.start();
